import React, { createContext, useContext, useState, useCallback } from 'react';
import { cachedApiService } from '../services/cachedApi';

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
    // State management
    const [profiles, setProfiles] = useState([]);
    const [currentProfile, setCurrentProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchFilters, setSearchFilters] = useState({
        search: '',
        tags: [],
        tagOperator: 'AND',
        includeUntagged: false,
        lifecycleStage: '',
        isActive: '',
        engagementScoreMin: null,
        engagementScoreMax: null,
        responseRateMin: null,
        responseRateMax: null,
        messageCountMin: null,
        messageCountMax: null,
        conversationCountMin: null,
        conversationCountMax: null,
        createdDateFrom: '',
        createdDateTo: '',
        lastContactFrom: '',
        lastContactTo: '',
        daysSinceLastContactMin: null,
        daysSinceLastContactMax: null,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
    });
    
    const [availableTags, setAvailableTags] = useState([]);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [lastSearchTime, setLastSearchTime] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0,
        hasNext: false,
        hasPrev: false
    });

    // Clear error helper
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Real-time update handlers
    const handleProfileUpdate = useCallback((updatedProfile) => {
        // Update current profile if it matches
        if (currentProfile && currentProfile._id === updatedProfile._id) {
            setCurrentProfile(updatedProfile);
        }

        // Update profiles list
        setProfiles(prevProfiles => 
            prevProfiles.map(profile => 
                profile._id === updatedProfile._id ? updatedProfile : profile
            )
        );
    }, [currentProfile]);

    const handleProfileCreated = useCallback((newProfile) => {
        // Add to profiles list if it doesn't exist
        setProfiles(prevProfiles => {
            const exists = prevProfiles.some(profile => profile._id === newProfile._id);
            return exists ? prevProfiles : [newProfile, ...prevProfiles];
        });
    }, []);

    const handleProfileDeleted = useCallback((profileId) => {
        // Remove from profiles list
        setProfiles(prevProfiles => 
            prevProfiles.filter(profile => profile._id !== profileId)
        );

        // Clear current profile if it's the deleted one
        if (currentProfile && currentProfile._id === profileId) {
            setCurrentProfile(null);
        }
    }, [currentProfile]);

    const handleProfileTagsUpdate = useCallback((profileId, tags) => {
        // Update current profile if it matches
        if (currentProfile && currentProfile._id === profileId) {
            setCurrentProfile(prev => ({ ...prev, tags }));
        }

        // Update profiles list
        setProfiles(prevProfiles => 
            prevProfiles.map(profile => 
                profile._id === profileId ? { ...profile, tags } : profile
            )
        );
    }, [currentProfile]);

    // Search profiles with filters and pagination
    const searchProfiles = useCallback(async (filters = {}, page = 1, limit = 50, append = false) => {
        try {
            setLoading(true);
            setError(null);

            // Get the current searchFilters from state
            const currentFilters = searchFilters;
            const searchParams = {
                ...currentFilters,
                ...filters,
                page,
                limit
            };

            // Clean up empty filter values
            const cleanedParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
                if (value !== null && value !== undefined && value !== '' && 
                    !(Array.isArray(value) && value.length === 0)) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const response = await cachedApiService.searchProfiles(cleanedParams);
            
            if (response.success) {
                // Either append to existing profiles or replace them
                if (append && page > 1) {
                    setProfiles(prev => [...prev, ...response.data]);
                } else {
                    setProfiles(response.data);
                }
                
                setPagination(response.pagination);
                
                // Only update searchFilters if there are actual filter changes
                if (Object.keys(filters).length > 0) {
                    setSearchFilters(prev => ({ ...prev, ...filters }));
                }
                
                setLastSearchTime(new Date().toISOString());
                
                // Update available tags if included in response
                if (response.availableTags) {
                    setAvailableTags(response.availableTags);
                }
            } else {
                throw new Error(response.message || 'Failed to search profiles');
            }

            return response;
        } catch (err) {
            setError(err.message || 'Failed to search profiles');
            console.error('Search profiles error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []); // Remove searchFilters dependency to prevent infinite loops
    
    // Load available tags from analytics endpoint (working endpoint)
    const loadAvailableTags = useCallback(async () => {
        try {
            const response = await cachedApiService.getProfileAnalytics();
            if (response.success && response.data.topTags) {
                // Keep tags as objects or convert strings to objects
                const tags = response.data.topTags.map(tag => {
                    if (typeof tag === 'string') {
                        return { name: tag };
                    } else if (tag._id && !tag.name) {
                        return { name: tag._id };
                    }
                    return tag;
                });
                setAvailableTags(tags);
            }
        } catch (err) {
            console.error('Failed to load available tags:', err);
        }
    }, []);
    
    // Search tags using available tags (client-side filtering)
    const searchTags = useCallback(async (query) => {
        try {
            // Get current availableTags from state to avoid dependency issues
            const currentTags = availableTags;
            const filtered = currentTags.filter(tag => {
                const tagName = typeof tag === 'string' ? tag : (tag.name || tag);
                return tagName.toLowerCase().includes(query.toLowerCase());
            }).slice(0, 10);
            setSearchSuggestions(filtered);
            return filtered;
        } catch (err) {
            console.error('Failed to search tags:', err);
        }
        return [];
    }, []); // Remove availableTags dependency
    
    // Export search results with enhanced formats
    const exportSearchResults = useCallback(async (options = {}) => {
        try {
            setLoading(true);
            setError(null);
            
            const {
                format = 'csv',
                includeFields = 'all',
                customFields = [],
                includeMetrics = false,
                filters = null
            } = options;
            
            // Use current profiles for export
            const dataToExport = profiles; // This should be fine as it's within the function
            const timestamp = new Date().toISOString().split('T')[0];
            
            if (format === 'csv') {
                // Define available fields
                const allFields = [
                    { key: 'name', label: 'Name', getValue: (p) => p.name || '' },
                    { key: 'phoneNumber', label: 'Phone', getValue: (p) => p.phoneNumber || p.phone || '' },
                    { key: 'email', label: 'Email', getValue: (p) => p.email || '' },
                    { key: 'tags', label: 'Tags', getValue: (p) => (p.tags || []).map(tag => tag.name || tag).join(';') },
                    { key: 'lifecycleStage', label: 'Lifecycle Stage', getValue: (p) => p.lifecycle?.lifecycle_stage || '' },
                    { key: 'isActive', label: 'Active', getValue: (p) => p.lifecycle?.isActive ? 'Yes' : 'No' },
                    { key: 'createdAt', label: 'Created Date', getValue: (p) => p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '' },
                    { key: 'lastContactAt', label: 'Last Contact', getValue: (p) => p.lifecycle?.lastContactAt ? new Date(p.lifecycle.lastContactAt).toLocaleDateString() : '' },
                    { key: 'daysSinceFirstContact', label: 'Days Since First Contact', getValue: (p) => p.lifecycle?.daysSinceFirstContact || '' },
                    { key: 'daysSinceLastContact', label: 'Days Since Last Contact', getValue: (p) => p.lifecycle?.daysSinceLastContact || '' }
                ];

                // Add metrics fields if requested
                if (includeMetrics) {
                    allFields.push(
                        { key: 'engagementScore', label: 'Engagement Score', getValue: (p) => p.scoring?.engagementScore || '' },
                        { key: 'completenessScore', label: 'Completeness Score', getValue: (p) => p.scoring?.completenessScore || '' },
                        { key: 'messageCount', label: 'Total Messages', getValue: (p) => p.behavior?.messageCount?.total || '' },
                        { key: 'responseRate', label: 'Response Rate', getValue: (p) => p.behavior?.responseRate ? (p.behavior.responseRate * 100).toFixed(1) + '%' : '' },
                        { key: 'avgResponseTime', label: 'Avg Response Time (min)', getValue: (p) => p.behavior?.avgResponseTime || '' }
                    );
                }

                // Add custom fields
                customFields.forEach(field => {
                    allFields.push({
                        key: field.key,
                        label: field.label,
                        getValue: (p) => p.customFields?.[field.key] || ''
                    });
                });

                // Select fields to export
                let fieldsToExport = allFields;
                if (includeFields !== 'all' && Array.isArray(includeFields)) {
                    fieldsToExport = allFields.filter(field => includeFields.includes(field.key));
                }

                const headers = fieldsToExport.map(field => field.label);
                const csvContent = [
                    headers.join(','),
                    ...dataToExport.map(profile => 
                        fieldsToExport.map(field => {
                            const value = field.getValue(profile);
                            return `"${String(value).replace(/"/g, '""')}"`;
                        }).join(',')
                    )
                ].join('\n');
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `profiles_export_${timestamp}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

            } else if (format === 'json') {
                // JSON export
                const jsonContent = JSON.stringify(dataToExport, null, 2);
                const blob = new Blob([jsonContent], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `profiles_export_${timestamp}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

            } else if (format === 'xlsx') {
                // Simple Excel-compatible format (tab-separated values)
                const allFields = [
                    { key: 'name', label: 'Name', getValue: (p) => p.name || '' },
                    { key: 'phoneNumber', label: 'Phone', getValue: (p) => p.phoneNumber || p.phone || '' },
                    { key: 'email', label: 'Email', getValue: (p) => p.email || '' },
                    { key: 'tags', label: 'Tags', getValue: (p) => (p.tags || []).map(tag => tag.name || tag).join('; ') },
                    { key: 'lifecycleStage', label: 'Lifecycle Stage', getValue: (p) => p.lifecycle?.lifecycle_stage || '' },
                    { key: 'engagementScore', label: 'Engagement Score', getValue: (p) => p.scoring?.engagementScore || '' },
                    { key: 'createdAt', label: 'Created Date', getValue: (p) => p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '' }
                ];

                const headers = allFields.map(field => field.label);
                const tsvContent = [
                    headers.join('\t'),
                    ...dataToExport.map(profile => 
                        allFields.map(field => field.getValue(profile)).join('\t')
                    )
                ].join('\n');
                
                const blob = new Blob([tsvContent], { type: 'application/vnd.ms-excel' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `profiles_export_${timestamp}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

            } else if (format === 'summary') {
                // Export summary report
                const stats = {
                    exportDate: new Date().toISOString(),
                    totalProfiles: dataToExport.length,
                    filters: searchFilters, // Using current state
                    summary: {
                        byLifecycleStage: {},
                        byTags: {},
                        engagementDistribution: { high: 0, medium: 0, low: 0 },
                        activityStatus: { active: 0, inactive: 0 }
                    },
                    profiles: dataToExport.map(profile => ({
                        id: profile._id,
                        name: profile.name,
                        phone: profile.phoneNumber || profile.phone,
                        email: profile.email,
                        tags: (profile.tags || []).map(tag => tag.name || tag),
                        lifecycleStage: profile.lifecycle?.lifecycle_stage,
                        engagementScore: profile.scoring?.engagementScore,
                        isActive: profile.lifecycle?.isActive,
                        createdAt: profile.createdAt,
                        lastContactAt: profile.lifecycle?.lastContactAt
                    }))
                };

                // Calculate summary statistics
                dataToExport.forEach(profile => {
                    // Lifecycle stages
                    const stage = profile.lifecycle?.lifecycle_stage || 'unknown';
                    stats.summary.byLifecycleStage[stage] = (stats.summary.byLifecycleStage[stage] || 0) + 1;

                    // Tags
                    (profile.tags || []).forEach(tag => {
                        const tagName = tag.name || tag;
                        stats.summary.byTags[tagName] = (stats.summary.byTags[tagName] || 0) + 1;
                    });

                    // Engagement distribution
                    const engagement = profile.scoring?.engagementScore || 0;
                    if (engagement >= 70) stats.summary.engagementDistribution.high++;
                    else if (engagement >= 40) stats.summary.engagementDistribution.medium++;
                    else stats.summary.engagementDistribution.low++;

                    // Activity status
                    if (profile.lifecycle?.isActive) stats.summary.activityStatus.active++;
                    else stats.summary.activityStatus.inactive++;
                });

                const jsonContent = JSON.stringify(stats, null, 2);
                const blob = new Blob([jsonContent], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `profiles_summary_${timestamp}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
            
            return { 
                success: true, 
                exported: dataToExport.length,
                format,
                timestamp 
            };
        } catch (err) {
            setError(err.message || 'Failed to export profiles');
            console.error('Export profiles error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []); // Remove dependencies to prevent re-creation
    
    // Get search statistics (using analytics endpoint)
    const getSearchStats = useCallback(async (filters = null) => {
        try {
            const response = await cachedApiService.getProfileStats();
            
            if (response.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to get search statistics');
            }
        } catch (err) {
            console.error('Get search stats error:', err);
            throw err;
        }
    }, []);

    // Get profile by phone number
    const getProfileByPhone = useCallback(async (phone) => {
        try {
            setLoading(true);
            setError(null);
            // Clear current profile when switching to avoid stale data
            setCurrentProfile(null);

            const response = await cachedApiService.getProfileByPhone(phone);
            
            if (response.success) {
                setCurrentProfile(response.data);
                return response.data;
            } else {
                // Check if it's a 404 (profile not found) vs other errors
                if (response.status === 404 || response.message?.includes('not found') || response.message?.includes('Profile not found')) {
                    // Profile doesn't exist - this is expected, not an error
                    setCurrentProfile(null);
                    setError(null);
                    return null;
                } else {
                    // Actual error occurred
                    throw new Error(response.message || 'Failed to get profile');
                }
            }
        } catch (err) {
            // Only set error for actual API errors, not for missing profiles
            if (!err.message?.includes('not found') && !err.message?.includes('Profile not found')) {
                setError(err.message || 'Failed to get profile');
            }
            console.error('Get profile error:', err);
            setCurrentProfile(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get profile by ID
    const getProfile = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            // Clear current profile when switching to avoid stale data
            setCurrentProfile(null);

            const response = await cachedApiService.getProfile(id);
            
            if (response.success) {
                setCurrentProfile(response.data);
                return response.data;
            } else {
                // Check if it's a 404 (profile not found) vs other errors
                if (response.status === 404 || response.message?.includes('not found') || response.message?.includes('Profile not found')) {
                    // Profile doesn't exist - this is expected, not an error
                    setCurrentProfile(null);
                    setError(null);
                    return null;
                } else {
                    // Actual error occurred
                    throw new Error(response.message || 'Failed to get profile');
                }
            }
        } catch (err) {
            // Only set error for actual API errors, not for missing profiles
            if (!err.message?.includes('not found') && !err.message?.includes('Profile not found')) {
                setError(err.message || 'Failed to get profile');
            }
            console.error('Get profile error:', err);
            setCurrentProfile(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Create new profile
    const createProfile = useCallback(async (profileData) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.createProfile(profileData);
            
            if (response.success) {
                // Add to profiles list if it matches current filters
                setProfiles(prev => [response.data, ...prev]);
                setCurrentProfile(response.data);
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to create profile');
            }
        } catch (err) {
            setError(err.message || 'Failed to create profile');
            console.error('Create profile error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Update profile
    const updateProfile = useCallback(async (id, updateData) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.updateProfile(id, updateData);
            
            if (response.success) {
                // Update in profiles list
                setProfiles(prev => 
                    prev.map(profile => 
                        profile._id === id ? response.data : profile
                    )
                );
                
                // Update current profile if it's the same
                if (currentProfile && currentProfile._id === id) {
                    setCurrentProfile(response.data);
                }
                
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to update profile');
            }
        } catch (err) {
            setError(err.message || 'Failed to update profile');
            console.error('Update profile error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentProfile]);

    // Delete profile
    const deleteProfile = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.deleteProfile(id);
            
            if (response.success) {
                // Remove from profiles list
                setProfiles(prev => prev.filter(profile => profile._id !== id));
                
                // Clear current profile if it's the same
                if (currentProfile && currentProfile._id === id) {
                    setCurrentProfile(null);
                }
                
                return true;
            } else {
                throw new Error(response.message || 'Failed to delete profile');
            }
        } catch (err) {
            setError(err.message || 'Failed to delete profile');
            console.error('Delete profile error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentProfile]);

    // Add tag to profile
    const addProfileTag = useCallback(async (profileId, tagName, type = 'manual') => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.apiService.addProfileTag(profileId, { tagName, type });
            
            if (response.success) {
                // Update profiles list
                setProfiles(prev =>
                    prev.map(profile => {
                        if (profile._id === profileId) {
                            return {
                                ...profile,
                                tags: [...(profile.tags || []), {
                                    name: tagName,
                                    type,
                                    addedAt: new Date().toISOString()
                                }]
                            };
                        }
                        return profile;
                    })
                );

                // Update current profile
                if (currentProfile && currentProfile._id === profileId) {
                    setCurrentProfile(prev => ({
                        ...prev,
                        tags: [...(prev.tags || []), {
                            name: tagName,
                            type,
                            addedAt: new Date().toISOString()
                        }]
                    }));
                }

                return response.data;
            } else {
                throw new Error(response.message || 'Failed to add tag');
            }
        } catch (err) {
            setError(err.message || 'Failed to add tag');
            console.error('Add tag error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentProfile]);

    // Remove tag from profile
    const removeProfileTag = useCallback(async (profileId, tagName) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.apiService.removeProfileTag(profileId, tagName);
            
            if (response.success) {
                // Update profiles list
                setProfiles(prev =>
                    prev.map(profile => {
                        if (profile._id === profileId) {
                            return {
                                ...profile,
                                tags: (profile.tags || []).filter(tag => tag.name !== tagName)
                            };
                        }
                        return profile;
                    })
                );

                // Update current profile
                if (currentProfile && currentProfile._id === profileId) {
                    setCurrentProfile(prev => ({
                        ...prev,
                        tags: (prev.tags || []).filter(tag => tag.name !== tagName)
                    }));
                }

                return true;
            } else {
                throw new Error(response.message || 'Failed to remove tag');
            }
        } catch (err) {
            setError(err.message || 'Failed to remove tag');
            console.error('Remove tag error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [currentProfile]);

    // Trigger auto-tagging for profile or all profiles
    const triggerAutoTagging = useCallback(async (profileId = null) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.apiService.triggerAutoTagging(profileId);
            
            if (response.success) {
                if (profileId) {
                    // Refresh the specific profile to get updated tags
                    await getProfile(profileId);
                } else {
                    // For global auto-tagging, refresh the profiles list
                    // Use current values from state
                    const currentPage = pagination.page;
                    const currentLimit = pagination.limit; 
                    const currentFilters = searchFilters;
                    await searchProfiles(currentFilters, currentPage, currentLimit);
                }
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to trigger auto-tagging');
            }
        } catch (err) {
            setError(err.message || 'Failed to trigger auto-tagging');
            console.error('Auto-tagging error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getProfile]); // Remove problematic dependencies

    // Get profile statistics
    const getProfileStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.getProfileStats();
            
            if (response.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to get profile statistics');
            }
        } catch (err) {
            setError(err.message || 'Failed to get profile statistics');
            console.error('Get profile stats error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Bulk update profiles
    const bulkUpdateProfiles = useCallback(async (profileIds, updateData) => {
        try {
            setLoading(true);
            setError(null);

            const response = await cachedApiService.apiService.bulkUpdateProfiles({ profileIds, updateData });
            
            if (response.success) {
                // Refresh profiles list
                // Use current values from state
                const currentPage = pagination.page;
                const currentLimit = pagination.limit; 
                const currentFilters = searchFilters;
                await searchProfiles(currentFilters, currentPage, currentLimit);
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to update profiles');
            }
        } catch (err) {
            setError(err.message || 'Failed to update profiles');
            console.error('Bulk update error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []); // Remove all dependencies to prevent re-creation

    // Reset state
    const resetState = useCallback(() => {
        setProfiles([]);
        setCurrentProfile(null);
        setError(null);
        setSearchFilters({
            search: '',
            tags: [],
            tagOperator: 'AND',
            includeUntagged: false,
            lifecycleStage: '',
            isActive: '',
            engagementScoreMin: null,
            engagementScoreMax: null,
            responseRateMin: null,
            responseRateMax: null,
            messageCountMin: null,
            messageCountMax: null,
            conversationCountMin: null,
            conversationCountMax: null,
            createdDateFrom: '',
            createdDateTo: '',
            lastContactFrom: '',
            lastContactTo: '',
            daysSinceLastContactMin: null,
            daysSinceLastContactMax: null,
            sortBy: 'updatedAt',
            sortOrder: 'desc'
        });
        setAvailableTags([]);
        setSearchSuggestions([]);
        setLastSearchTime(null);
        setPagination({
            page: 1,
            limit: 50,
            total: 0,
            pages: 0,
            hasNext: false,
            hasPrev: false
        });
    }, []);

    const value = {
        // State
        profiles,
        currentProfile,
        loading,
        error,
        searchFilters,
        pagination,
        availableTags,
        searchSuggestions,
        lastSearchTime,

        // Actions
        searchProfiles,
        getProfile,
        getProfileByPhone,
        createProfile,
        updateProfile,
        deleteProfile,
        addProfileTag,
        removeProfileTag,
        triggerAutoTagging,
        getProfileStats,
        bulkUpdateProfiles,
        
        // Enhanced search actions
        loadAvailableTags,
        searchTags,
        exportSearchResults,
        getSearchStats,
        
        // Helpers
        clearError,
        resetState,
        setCurrentProfile,
        setSearchFilters,
        setAvailableTags,
        setSearchSuggestions,
        
        // Direct state setters for debugging
        setProfiles,
        setLoading,
        setError,
        setPagination,

        // Real-time update handlers
        handleProfileUpdate,
        handleProfileCreated,
        handleProfileDeleted,
        handleProfileTagsUpdate
    };

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};

// Custom hook to use profile context
export const useProfiles = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfiles must be used within a ProfileProvider');
    }
    return context;
};

export default ProfileContext;